<template>
    <div class="artist-credits" v-bind="$attrs" :class="{ 'artist-credits--pocket': pocket }">
        <div class="container">
            <ProButton
                @click="open ? (open = false) : (open = true)"
                class="artist-credits__heading text-subheading text-subheading--sm"
                :class="{ 'artist-credits__heading--open': open }"
            >
                {{ $t("global.artist_credits.title") }}<ProIcon icon="arrow-down" />
            </ProButton>
        </div>
        <div class="fade-wrapper" :class="{ 'fade-wrapper--visible': open }" :aria-hidden="!open">
            <div class="container">
                <div class="artist-credits__slider">
                    <div class="artist-credits__nav" v-if="slidesToShow < slideCount">
                        <ProButton
                            @click="navigateSlides(-1)"
                            :disabled="currentSlide == 1"
                            class="artist-credits__btn artist-credits__nav-prev"
                            :aria-label="`${$t('global.previous')} – ${
                                slidesToShow < 2 ? $t('global.artist_credits.single', a11yNavInfo) : $t('global.artist_credits.multiple', a11yNavInfo)
                            }`"
                        >
                            <ProIcon icon="cta-right" />
                        </ProButton>
                        <ProButton
                            @click="navigateSlides(1)"
                            :disabled="currentSlide >= scrollLimit"
                            class="artist-credits__btn artist-credits__nav-next"
                            :aria-label="`${$t('global.next')} – ${
                                slidesToShow < 2 ? $t('global.artist_credits.single', a11yNavInfo) : $t('global.artist_credits.multiple', a11yNavInfo)
                            }`"
                        >
                            <ProIcon icon="cta-right" />
                        </ProButton>
                    </div>
                    <div
                        class="artist-credits__slider-container"
                        ref="sliderWindow"
                        aria-live="polite"
                        @touchstart="press(), toggleVerticalScroll()"
                        @touchend="release(), toggleVerticalScroll()"
                        @touchmove="move()"
                        @mousedown="press()"
                        @mouseup="release()"
                        @mouseleave="release()"
                        @mousemove="move()"
                    >
                        <div class="artist-credits__stage">
                            <div
                                ref="track"
                                class="artist-credits__track"
                                :class="{ 'drag-active': dragActive }"
                                :style="`width: ${trackWidth}px; transform: translate3d(${dragActive ? dragPosition * -1 : trackPosition * -1}px, 0, 0)`"
                            >
                                <ul class="artist-credits__item-list">
                                    <li
                                        v-for="(art, i) in credits"
                                        :key="i"
                                        :style="`width: ${slideWidth}px`"
                                        class="artist-credits__item"
                                        :aria-label="`${art.artist} – ${art.artwork}`"
                                        :aria-hidden="visibleSlides.includes(i + 1) ? null : true"
                                    >
                                        <div
                                            class="artist-credits__item-artwork"
                                            :style="{ backgroundImage: `url(${art.thumbnail})` }"
                                            :class="`artist-credits__item-artwork--${art.weighting}`"
                                        ></div>
                                        <h3 class="sr-only artist-credits__a11y-label">{{ art.artist }} – {{ art.artwork }}</h3>
                                        <div class="artist-credits__item-info" aria-hidden="true">
                                            <h3 class="artist-credits__item-artist">{{ art.artist }}</h3>
                                            <p class="artist-credits__item-name">
                                                <em>{{ art.artwork }}</em>
                                            </p>

                                            <p v-if="art.commission != null" class="artist-credits__item-commission">
                                                <span v-if="art.commission">{{ $t("global.artist_credits.artwork_commissioned") }}</span>
                                                <span v-else>{{ $t("global.artist_credits.artwork_licensed") }}</span>
                                            </p>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import _, { debounce } from "underscore";

export default {
    data: () => ({
        open: false,
        sliderWidth: 0,
        slidesToShow: 3,
        currentSlide: 1,
        mouseDrag: 0,
        clickPosition: 0,
        dragActive: false,
        resizeObserver: null,
        visibleSlides: [],
        a11yNavInfo: "",
    }),

    props: {
        pocket: Boolean,
    },

    methods: {
        toggleArtists() {
            this.open = !this.open;
        },

        minMax(value, low, high) {
            return Math.min(Math.max(value, low), high);
        },

        toggleVerticalScroll() {
            //for Safari touhc scrolling vertical lock.
            if (this.dragActive) {
                document.querySelector("body").classList.add("scroll-lock");
            } else {
                document.querySelector("body").classList.remove("scroll-lock");
            }
        },

        navigateSlides(amount) {
            this.currentSlide = this.minMax(this.currentSlide + amount, 1, this.scrollLimit);
        },

        setUpResizeObserver(object) {
            this.resizeObserver = new ResizeObserver(
                _.debounce((entries) => {
                    for (let entry of entries) {
                        const entrySize = entry.contentBoxSize
                            ? entry.contentBoxSize[0]
                                ? entry.contentBoxSize[0].inlineSize
                                : entry.contentBoxSize.inlineSize
                            : entry.contentRect.width;
                        //contentBoxSize should be used, but isn't supported in all browsers
                        this.sliderWidth = entrySize;
                        this.slidesToShow = this.breakpointSwitch(entrySize);
                        this.updateVisibleSlideRange();
                    }
                }),
                200
            );
            this.resizeObserver.observe(object);
        },

        breakpointSwitch(size) {
            const objectSize = size;
            if (objectSize <= 480) {
                return 1;
            } else if (objectSize <= 750) {
                return 1.5;
            } else if (objectSize <= 920) {
                return 2;
            } else {
                return 3;
            }
            //options for break points, and how many slide/s to show for each (can be a fraction)
        },

        press() {
            if (this.scrollable) {
                this.dragActive = true;
                this.clickPosition = event.pageX;
            }
        },

        release() {
            if (this.scrollable) {
                this.dragActive = false;

                const endSlide = Math.round((this.dragPosition / this.trackWidth) * this.slideCount + 1);
                this.currentSlide = this.minMax(endSlide, 1, this.scrollLimit);

                this.clickPosition = 0;
                this.mouseDrag = 0;
            }
        },

        move() {
            if (this.dragActive && this.scrollable) {
                this.mouseDrag = (event.pageX - this.clickPosition) * 1.4;
            }
        },

        updateVisibleSlideRange() {
            const low = this.currentSlide;
            const high = this.currentSlide + (this.slidesToShow - 1);

            this.visibleSlides = [];

            for (let i = low; i <= high; i++) {
                this.visibleSlides.push(i);
            }

            this.updateNavInfo();
        },

        updateNavInfo() {
            this.a11yNavInfo = {
                current: this.currentSlide,
                lastVisible: this.visibleSlides[this.visibleSlides.length - 1],
                total: this.slideCount,
            };
        },
    },

    mounted() {
        this.setUpResizeObserver(this.$refs.sliderWindow);
        this.updateVisibleSlideRange();

        document.addEventListener("keydown", () => {
            if (event.key == "ArrowRight" || event.key == "ArrowLeft") {
                this.navigateSlides(event.key == "ArrowRight" ? 1 : -1);
            }
        });
    },

    computed: {
        ...mapGetters({
            credits: "creditMedia/getMedia",
        }),
        slideWidth() {
            return Math.round(this.sliderWidth / this.slidesToShow);
        },
        slideCount() {
            return this.credits.length;
        },
        trackWidth() {
            return this.credits.length * this.slideWidth;
        },
        trackPosition() {
            return (this.currentSlide - 1) * this.slideWidth;
        },
        dragPosition() {
            return this.minMax(this.trackPosition - this.mouseDrag, 0, (this.scrollLimit - 1) * this.slideWidth);
        },
        scrollable() {
            return this.slideCount > this.slidesToShow;
        },
        scrollLimit() {
            return this.slidesToShow >= 2 ? this.slideCount - this.slidesToShow + 1 : this.slideCount;
        },
    },

    watch: {
        currentSlide() {
            this.updateVisibleSlideRange();
        },
    },

    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
};
</script>
